import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { RowThumbnailButton } from 'features/BillOfMaterials/components/BomItemThumbnailButton'
import { fetchProject } from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { bomItemSelector } from 'features/BillOfMaterials/store/selectors/bomItemSelector'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { isEqual } from 'lodash'
import { BomItemType, PartTypeRow } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import {
  RootAppState,
  useAppDispatch,
  useAppSelector,
} from 'store/configureStore'

/** Activity is deletable if it's not raw material, or if it's powder coating */
const isDeletableActivity = (a: {
  usesRawMaterial?: boolean
  primaryWorkingStep?: WorkingStepType
}) =>
  !a.usesRawMaterial || a.primaryWorkingStep === WorkingStepType.PowderCoating

type WorkingStepSelection = {
  key: string
  primaryWorkingStep: WorkingStepType
  resourceName?: string
}

const getWorkingStepSignature = (activity: {
  primaryWorkingStep?: WorkingStepType
  resource?: { name?: string }
}) => JSON.stringify([activity.primaryWorkingStep, activity.resource?.name])

const getWorkingStepKey = (
  activity: {
    primaryWorkingStep?: WorkingStepType
    resource?: { name?: string }
  },
  occurrence: number,
) =>
  JSON.stringify([
    activity.primaryWorkingStep,
    activity.resource?.name,
    occurrence,
  ])

const getActivityWorkingStepKey = (
  activities: Array<{
    primaryWorkingStep?: WorkingStepType
    resource?: { name?: string }
    usesRawMaterial?: boolean
  }>,
  index: number,
) => {
  const activity = activities[index]
  const occurrence = activities
    .slice(0, index)
    .filter(
      (previous) =>
        isDeletableActivity(previous) &&
        getWorkingStepSignature(previous) === getWorkingStepSignature(activity),
    ).length
  return getWorkingStepKey(activity, occurrence)
}

export type DeleteWorkingStepsDialogProps = {
  selectedPointers: BomItemPointer[]
  onClose?: () => void
}

type Props = DeleteWorkingStepsDialogProps & {
  open: boolean
  onClose: () => void
}

export const DeleteWorkingStepsDialog = ({
  selectedPointers,
  open,
  onClose,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()
  const isBuyerView = useAppSelector(
    projectSelectors.projectInBuyerViewSelector,
  )

  const [selectedWorkingSteps, setSelectedWorkingSteps] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const projectId = useAppSelector(
    (state: RootAppState) => state.project.activeProject?.id,
  )

  const bomItems = useAppSelector(
    (state: RootAppState) =>
      selectedPointers
        .map((p) => bomItemSelector(state, p))
        .filter(
          (item) =>
            item &&
            (item.type === BomItemType.partType ||
              item.type === BomItemType.partInstance),
        ) as PartTypeRow[],
    isEqual,
  )

  // find working steps common to ALL selected items, excluding raw material
  const commonWorkingSteps = useMemo(() => {
    if (bomItems.length === 0) return []

    const signatureCounts = bomItems.map((item) => {
      const counts = new Map<string, number>()
      item.activities?.filter(isDeletableActivity).forEach((activity) => {
        const signature = getWorkingStepSignature(activity)
        counts.set(signature, (counts.get(signature) || 0) + 1)
      })
      return counts
    })

    const firstItemActivities =
      bomItems[0].activities?.filter(isDeletableActivity) || []
    const occurrences = new Map<string, number>()

    return firstItemActivities.flatMap((activity) => {
      const signature = getWorkingStepSignature(activity)
      const occurrence = occurrences.get(signature) || 0
      occurrences.set(signature, occurrence + 1)
      if (
        !signatureCounts.every(
          (counts) => (counts.get(signature) || 0) > occurrence,
        )
      )
        return []

      return [
        {
          key: getWorkingStepKey(activity, occurrence),
          primaryWorkingStep: activity.primaryWorkingStep,
          resourceName: activity.resource?.name,
        } satisfies WorkingStepSelection,
      ]
    })
  }, [bomItems])

  // items that have at least one of the selected working steps (for preview)
  const affectedItems = useMemo(
    () =>
      bomItems.filter((item) =>
        item.activities?.some(
          (activity, index, activities) =>
            isDeletableActivity(activity) &&
            selectedWorkingSteps.includes(
              getActivityWorkingStepKey(activities, index),
            ),
        ),
      ),
    [bomItems, selectedWorkingSteps],
  )

  const handleToggle = useCallback((workingStepKey: string) => {
    setSelectedWorkingSteps((prev) =>
      prev.includes(workingStepKey)
        ? prev.filter((key) => key !== workingStepKey)
        : [...prev, workingStepKey],
    )
  }, [])

  const handleSave = useCallback(async () => {
    if (selectedWorkingSteps.length === 0) return

    setSaving(true)
    try {
      const controller = new WorkingStepsController()

      // Build boMItemAndActivityIds by finding matching activities on each bomItem
      const boMItemAndActivityIds = bomItems.flatMap(
        (item) =>
          item.activities
            ?.filter(
              (activity, index, activities) =>
                isDeletableActivity(activity) &&
                selectedWorkingSteps.includes(
                  getActivityWorkingStepKey(activities, index),
                ),
            )
            .map((a) => ({
              boMItemId: item.id,
              activityId: a.id,
            })) || [],
      )

      await controller.DeleteWorkingSteps(projectId, boMItemAndActivityIds)

      dispatch(fetchProject({ projectId }))
      onClose()
    } catch (err) {
      ShowException('delete working steps', err)
    } finally {
      setSaving(false)
    }
  }, [selectedWorkingSteps, projectId, selectedPointers, dispatch, onClose])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t(
          'project:delete-working-steps-title',
          'Delete working steps from selected items',
        )}
      </DialogTitle>
      <DialogContent sx={{ height: '60vh', overflow: 'hidden', p: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, height: '100%', p: 2 }}>
          {/* Left column: Common working steps */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t('project:delete-working-steps-common', 'Common working steps')}
            </Typography>

            {commonWorkingSteps.length === 0 ? (
              <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                {t(
                  'project:delete-working-steps-none',
                  'No common working steps found across selected items',
                )}
              </Box>
            ) : (
              <List dense sx={{ flex: 1, overflow: 'auto' }}>
                {commonWorkingSteps.map((ws) => {
                  const checked = selectedWorkingSteps.includes(ws.key)
                  return (
                    <ListItem key={ws.key} disablePadding>
                      <ListItemButton onClick={() => handleToggle(ws.key)}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={checked}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ minWidth: 36, mr: 2 }}>
                          <WorkingStepIconSvg
                            workingStepType={ws.primaryWorkingStep}
                            disableTooltip
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={getLocalizedWorkingStep(
                            ws.primaryWorkingStep,
                          )}
                          secondary={isBuyerView ? '' : ws.resourceName}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
              </List>
            )}
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Right column: Affected items */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t(
                'project:delete-working-steps-affected',
                'Affected items ({{count}})',
                { count: affectedItems.length },
              )}
            </Typography>
            <List dense sx={{ flex: 1, overflow: 'auto' }}>
              {affectedItems.map((item) => {
                const pointer = selectedPointers.find((p) => p.id === item.id)
                return (
                  <ListItem
                    key={item.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Typography variant="caption">{item.rowNumber}.</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RowThumbnailButton
                        bomItemPointer={pointer}
                        size="small"
                        disableClick
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {item.name || item.id}
                        </Typography>
                        {item.activities?.length > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                            }}
                          >
                            {item.activities
                              .map((activity, index, activities) => ({
                                activity,
                                key: getActivityWorkingStepKey(
                                  activities,
                                  index,
                                ),
                              }))
                              .filter(({ activity }) =>
                                isDeletableActivity(activity),
                              )
                              .map(({ activity, key }) => (
                                <WorkingStepIconSvg
                                  key={activity.id}
                                  workingStepType={activity.primaryWorkingStep}
                                  attributes={{
                                    width: '24px',
                                    height: '24px',
                                    style: selectedWorkingSteps.includes(key)
                                      ? {
                                          opacity: 0.3,
                                          textDecoration: 'line-through',
                                        }
                                      : undefined,
                                  }}
                                />
                              ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                )
              })}
            </List>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:cancel', 'Cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSave}
          disabled={saving || selectedWorkingSteps.length === 0}
        >
          {saving
            ? t('common:saving', 'Saving...')
            : t(
                'project:delete-working-steps-save',
                'Delete {{count}} working steps',
                { count: selectedWorkingSteps.length },
              )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
